/**
 * AgedCarePage – container for the aged care flow: explanation view or funding analyser.
 * Renders AgedCareCostsExplained first, then AgedCareAnalyser (passed in from App).
 */
import React, { useState, useEffect } from 'react';
import { scrollAppToTop } from '../../utils/scrollAppToTop.ts';
import AgedCareCostsExplained from './AgedCareCostsExplained.tsx';
import type { AgedCareAnalyserProps } from './AgedCareAnalyser.tsx';

export interface AgedCarePageProps extends Omit<AgedCareAnalyserProps, 'onBack'> {
    /** AgedCareAnalyser is injected from App.js rather than imported directly here. */
    AgedCareAnalyser: React.ComponentType<AgedCareAnalyserProps>;
}

const AgedCarePage = ({
    setActivePage,
    factFind,
    setFactFind,
    strategyOptions,
    setStrategyOptions,
    legacyYears,
    setLegacyYears,
    selectedStrategy,
    setSelectedStrategy,
    coupleValidation,
    setCoupleValidation,
    savedStrategies,
    setSavedStrategies,
    savedCaseStudies,
    setSavedCaseStudies,
    isProfessionalJourney = false,
    professionalCaseId = null,
    onSoaGenerated = null,
    onStrategySaved = null,
    AgedCareAnalyser,
}: AgedCarePageProps) => {
    // The guide first (owner's call, 2026-09-22, having seen the analyser open directly): a client
    // reads "Understanding Aged Care Costs" and continues to the analyser, as before. The analyser's
    // own "Read the guide" links bring them back here whenever they want it again.
    const [view, setView] = useState<'explanation' | 'calculator'>('explanation');

    useEffect(() => {
        if (view === 'calculator') {
            scrollAppToTop();
        }
    }, [view]);

    if (view === 'explanation') {
        return (
            <AgedCareCostsExplained
                setActivePage={setActivePage}
                onContinue={() => setView('calculator')}
                factFind={factFind}
            />
        );
    }

    return (
        <>
            <AgedCareAnalyser
                setActivePage={setActivePage}
                factFind={factFind}
                setFactFind={setFactFind}
                strategyOptions={strategyOptions}
                setStrategyOptions={setStrategyOptions}
                onBack={() => setView('explanation')}
                legacyYears={legacyYears}
                setLegacyYears={setLegacyYears}
                selectedStrategy={selectedStrategy}
                setSelectedStrategy={setSelectedStrategy}
                coupleValidation={coupleValidation}
                setCoupleValidation={setCoupleValidation}
                savedStrategies={savedStrategies}
                setSavedStrategies={setSavedStrategies}
                savedCaseStudies={savedCaseStudies}
                setSavedCaseStudies={setSavedCaseStudies}
                isProfessionalJourney={isProfessionalJourney}
                professionalCaseId={professionalCaseId}
                onSoaGenerated={onSoaGenerated}
                onStrategySaved={onStrategySaved}
            />
        </>
    );
};

export default AgedCarePage;
